import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import useNotificationStore, { NOTIFICATION_PAGE_SIZE, selectTenantNotifications } from '../store/notificationStore'
import { isKnownNotificationLink } from '../lib/notificationLinks'
import { useNotificationTenantId } from './useNotificationTenantId'

// One click reads a notification and goes where it points — the same on the
// history page and in the bell dropdown. A link the manager has no page for
// is not followed.
export function useOpenNotification() {
  const navigate = useNavigate()
  const markRead = useNotificationStore((s) => s.markRead)
  return useCallback(
    (notification) => {
      markRead(notification.id)
      if (isKnownNotificationLink(notification.link)) navigate(notification.link)
    },
    [markRead, navigate]
  )
}

// What the bell dropdown shows, for AppShell and DesktopShell alike. The
// state itself lives in the shared notification store, kept in step with the
// server by NotificationSync (mounted once in LangLayout): the shells remount
// on every route change, so they only read it and call its actions.
export function useBellNotifications() {
  const tenantId = useNotificationTenantId()
  const loaded = useNotificationStore((s) => selectTenantNotifications(s, tenantId).items)
  // The history page can page far past this; the dropdown stays a glance.
  const notifications = useMemo(() => loaded.slice(0, NOTIFICATION_PAGE_SIZE), [loaded])
  const unreadCount = useNotificationStore((s) => selectTenantNotifications(s, tenantId).unreadCount)
  const refresh = useNotificationStore((s) => s.refresh)
  const markAllRead = useNotificationStore((s) => s.markAllRead)
  const remove = useNotificationStore((s) => s.remove)
  const open = useOpenNotification()
  return { notifications, unreadCount, refresh, markAllRead, remove, open }
}
